import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from '@linaria/core';
import cloneDeep from 'lodash/cloneDeep.js';
import lottie from 'lottie-web';
import animationData from './loader.json';

const loaderClassnames = {
  root: css`
    width: 128px;
    height: auto;
  `,
  absolute: css`
    transform: translate(-50%, -50%);
    position: absolute;
    top: 50%;
    left: 50%;
  `,
  relative: css`
    position: relative;
  `,
  small: css`
    width: 64px;
    height: auto;
  `,
};

const Loader = ({
  type = 'normal',
  loading = true,
  style: overwriteStyles = {},
  position = 'absolute',
  onComplete,
  completeAfterTime,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!loading) return;

    const instance = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: typeof onComplete !== 'function',
      autoplay: true,
      animationData: cloneDeep(animationData),
    });

    let timeout;
    if (completeAfterTime && typeof onComplete === 'function') {
      timeout = setTimeout(() => {
        onComplete();
      }, completeAfterTime);
    } else if (!completeAfterTime && typeof onComplete === 'function') {
      instance.addEventListener('complete', onComplete);
    }

    return () => {
      instance?.removeEventListener('complete', onComplete);
      instance?.destroy();

      if (timeout) clearTimeout(timeout);
    };
  }, [loading, onComplete, completeAfterTime]);

  const className = cx(loaderClassnames.root, loaderClassnames[type], loaderClassnames[position]);

  if (!loading) return null;

  return <div ref={containerRef} className={className} style={overwriteStyles} role="progressbar" />;
};

Loader.propTypes = {
  type: PropTypes.oneOf(['normal', 'small']),
  loading: PropTypes.bool,
  style: PropTypes.object,
  position: PropTypes.oneOf(['absolute', 'relative']),
  onComplete: PropTypes.func,
  completeAfterTime: PropTypes.number,
};

export default Loader;
